import type { ChangeEvent } from "react";
import clsx from "clsx";
import { action, computed, makeObservable, observable } from "mobx";
import { injectable } from "tsyringe";
import { ViewModel } from "@orderofchaos/mvvm";
import { IconSize } from "../Icon/Icon";
import type { InputOnChangeData, InputProps, InputSize } from "./Input";
import styles from "./Input.module.css";

type InputElementProps = Omit<
  InputProps,
  | "label"
  | "labelClassName"
  | "value"
  | "className"
  | "textAlign"
  | "iconName"
  | "dirty"
  | "isValid"
  | "onIconClick"
  | "getRef"
  | "iconPosition"
  | "size"
  | "disabled"
  | "onChange"
>;

@injectable()
export class InputViewModel extends ViewModel<unknown, InputProps> {
  @observable
  private inFocus: boolean | null = null;

  constructor() {
    super();
    makeObservable(this);
  }

  @computed
  get label(): string | null {
    return this.viewProps.label ?? null;
  }

  @computed
  get value(): string {
    return this.viewProps.value ?? "";
  }

  @computed
  get labelClassName(): string {
    return this.viewProps.labelClassName || "";
  }

  @computed
  get className(): string {
    return this.viewProps.className || "";
  }

  @computed
  get textAlign(): "start" | "center" | "end" {
    return this.viewProps.textAlign || "start";
  }

  @computed
  get iconName(): InputProps["iconName"] {
    return this.viewProps.iconName;
  }

  @computed
  get dirty(): boolean {
    return this.viewProps.dirty ?? false;
  }

  @computed
  get isValid(): boolean {
    return this.viewProps.isValid ?? false;
  }

  @computed
  get onIconClick(): (() => unknown) | undefined {
    return this.viewProps.onIconClick;
  }

  @computed
  get getRef(): InputProps["getRef"] {
    return this.viewProps.getRef;
  }

  @computed
  get iconPosition(): "left" | "right" {
    return this.viewProps.iconPosition || "right";
  }

  @computed
  get size(): InputSize {
    return this.viewProps.size || IconSize.large;
  }

  @computed
  get disabled(): boolean {
    return this.viewProps.disabled ?? false;
  }

  @computed
  get iconElementTag(): "button" | "span" {
    return this.onIconClick ? "button" : "span";
  }

  @computed
  get inputElementProps(): InputElementProps {
    const {
      label: _label,
      labelClassName: _labelClassName,
      value: _value,
      className: _className,
      textAlign: _textAlign,
      iconName: _iconName,
      dirty: _dirty,
      isValid: _isValid,
      onIconClick: _onIconClick,
      getRef: _getRef,
      iconPosition: _iconPosition,
      size: _size,
      disabled: _disabled,
      onChange: _onChange,
      ...rest
    } = this.viewProps;

    return rest;
  }

  @computed
  get classes(): string {
    const hasErrorClass = this.dirty ? styles.error : "";
    const validationClass = this.isValid ? styles.valid : hasErrorClass;

    return clsx(
      styles.Input,
      validationClass,
      {
        [styles[this.textAlign]]: true,
        [styles[this.size]]: true,
        [styles[`iconPosition_${this.iconPosition}`]]: true,
      },
      this.className,
    );
  }

  @computed
  get labelClasses(): string {
    const isIconUnderLabel =
      Boolean(this.iconName) && this.iconPosition === "left";
    const labelInitAnimation = this.inFocus === null ? "" : styles.down;
    const animationDirection =
      isIconUnderLabel || this.inFocus || this.value
        ? styles.up
        : labelInitAnimation;

    return `${styles.placeholderLabel} ${animationDirection} ${this.labelClassName}`;
  }

  @action.bound
  onFocus(): void {
    this.inFocus = true;
  }

  @action.bound
  onBlur(): void {
    this.inFocus = false;
  }

  @action.bound
  onInputChange(event: ChangeEvent<HTMLInputElement>): void {
    if (!this.viewProps.onChange) {
      return;
    }

    const data: InputOnChangeData = {
      ...this.viewProps,
      value: event.target.value,
    };
    this.viewProps.onChange(event, data);
  }
}
